import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { TimeInput, Textarea, AlertNotice, ModalInfo } from 'shared/components';
import { isEmpty } from 'lodash';
import { ValidationService } from 'shared/services';
import { pauseErrorStrings, pauseValidationRules } from 'shared/validations';
import { IrregularityModel } from 'features/irregularities';
import uniqueId from 'lodash/uniqueId';
import { DATE_FORMATS, IRREGULARITIES_NOTES_MAX_LENGTH } from 'shared/constants';
import omit from 'lodash/omit';
import MaterialIcon from 'material-icons-react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';

import './AddPause.scss';

const pauseModel = {
  pauseDateTime: '',
  unpauseDateTime: '',
  pauseDescription: '',
};

const validTime = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return true;
  }
  return moment(endTime).diff(moment(startTime)) > 0;
};

class AddPause extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pauseModalOpen: false,
      currentPause: pauseModel,
      editPauseFlag: false,
      invalidTimeError: false,
      errors: {},
    };

    this.onAddPause = this.onAddPause.bind(this);
    this.togglePauseModal = this.togglePauseModal.bind(this);
    this.onChangePauseTime = this.onChangePauseTime.bind(this);
    this.onChangePauseDescription = this.onChangePauseDescription.bind(this);
    this.validateTime = this.validateTime.bind(this);
  }

  validateTime(currentPause) {
    return !validTime(currentPause.pauseDateTime, currentPause.unpauseDateTime);
  }

  onAddPause() {
    const { currentPause, editPauseFlag } = this.state;
    const { userId } = this.props;
    const pauseErrors = ValidationService.validate(
      currentPause,
      pauseValidationRules,
      pauseErrorStrings
    );
    const invalidTimeError = this.validateTime(currentPause);

    if (!isEmpty(pauseErrors) || invalidTimeError) {
      this.setState({
        errors: {
          ...pauseErrors,
        },
        invalidTimeError,
      });
      return;
    }

    const pauseTime = moment(currentPause.unpauseDateTime).diff(
      moment(currentPause.pauseDateTime),
      'seconds'
    );
    const duration = parseInt(pauseTime / 60) + (pauseTime % 60 > 0 ? 1 : 0);
    const typeId = this.props.irregularityTypes.find((irr) => irr.type === 'timing_pause').id;
    const irregularity = new IrregularityModel({
      testCenterEventId: +this.props.eventId,
      scope: 'room',
      notes: currentPause.pauseDescription,
      startDateTime: moment(currentPause.pauseDateTime).format(DATE_FORMATS.dateTimeZone),
      duration,
      submitPersonnel: { id: userId },
      irregularityRooms: [{ roomId: +this.props.roomId }],
      irregularityType: {
        id: typeId,
      },
    });
    const newPause = {
      id: uniqueId(),
      ...omit(currentPause, 'pauseDescription'),
      pausePersonnelId: userId,
      unpausePersonnelId: userId,
      irregularity,
    };

    this.props.addTimerPause(newPause, editPauseFlag);
    this.togglePauseModal();
  }

  togglePauseModal(editPause) {
    const pause = editPause
      ? {
          ...editPause,
          pauseDescription: editPause.irregularity.notes,
        }
      : pauseModel;
    this.setState((prevState) => ({
      pauseModalOpen: !prevState.pauseModalOpen,
      currentPause: pause,
      editPauseFlag: !!editPause,
      errors: prevState.pauseModalOpen ? {} : prevState.errors,
      invalidTimeError: false,
    }));
  }

  onChangePauseTime(time, name) {
    this.setState((prevState) => ({
      currentPause: {
        ...prevState.currentPause,
        [name]: time,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
      invalidTimeError: false,
    }));
  }

  onChangePauseDescription(e) {
    const { value } = e.target;
    this.setState((prevState) => ({
      currentPause: {
        ...prevState.currentPause,
        pauseDescription: value,
      },
      errors: {
        ...prevState.errors,
        pauseDescription: '',
      },
    }));
  }

  render() {
    const { pauseModalOpen, currentPause, editPauseFlag, errors, invalidTimeError } = this.state;
    const { timerPauseList, errorNoPause } = this.props;

    const modalLabelHeader = !editPauseFlag
      ? 'taskManagement.manualTimer.addPauseTime'
      : 'taskManagement.manualTimer.editPauseTime';

    return (
      <div className="pause-container">
        {!!timerPauseList &&
          !!timerPauseList.length &&
          timerPauseList.map((pause, idx) => (
            <div key={`timer-${idx}`} className="border-top-gray mt-3 pt-4">
              <p className="fw-bold">
                {this.props.t('taskManagement.manualTimer.pauseTimeLabel', { index: idx + 1 })}
              </p>
              <p className="gray-color fw-bold mb-1">
                {moment(pause.pauseDateTime).format('hh:mm a')} -{' '}
                {moment(pause.unpauseDateTime).format('hh:mm a')}
              </p>
              <p className="gray-color">{pause.irregularity.notes}</p>
              <div>
                <button
                  className="btn-link text-pacific-blue text-strong"
                  onClick={() => this.togglePauseModal(pause)}>
                  <span className="fw-bold">
                    <i className="fa fa-pencil" />
                    {this.props.t('common.labels.editInfo')}
                  </span>
                </button>
                <button
                  className="btn-link text-pacific-blue text-strong"
                  onClick={() => this.props.removeTimerPause(pause)}>
                  <span className="fw-bold">
                    <i className="fa fa-trash-o" />
                    {this.props.t('taskManagement.manualTimer.deletePauseLabel')}
                  </span>
                </button>
              </div>
            </div>
          ))}
        <div className="border-top-gray mt-3">
          <button
            className="pl-0 mt-3 btn-link text-pacific-blue text-strong add-button"
            onClick={() => this.togglePauseModal()}>
            <span className="fw-bold">
              <MaterialIcon icon="control_point" size="tiny" />
              {this.props.t('taskManagement.manualTimer.newPauseLabel')}
            </span>
          </button>
          {errorNoPause && (
            <p className="text-error font-size-12" key={errorNoPause[0]}>
              {this.props.t(errorNoPause[0])}
            </p>
          )}
        </div>
        <ModalInfo
          modalIsOpen={pauseModalOpen}
          closeModal={() => this.togglePauseModal()}
          headerTitle={modalLabelHeader}
          onSubmit={this.onAddPause}
          saveLabel={modalLabelHeader}
          className="modal-add-pause">
          <section className="add-pause">
            {invalidTimeError && (
              <AlertNotice
                message={this.props.t('taskManagement.manualTimer.chronologicalError')}
                type="error"
              />
            )}
            <TimeInput
              className="pb-4"
              dateTime={currentPause.pauseDateTime}
              icon="fa-play"
              label="taskManagement.manualTimer.pauseStartLabel"
              inputName="pauseDateTime"
              onChange={this.onChangePauseTime}
              error={errors.pauseDateTime}
            />
            <TimeInput
              className="pb-4"
              dateTime={currentPause.unpauseDateTime}
              icon="fa-stop"
              label="taskManagement.manualTimer.pauseStopLabel"
              inputName="unpauseDateTime"
              onChange={this.onChangePauseTime}
              error={errors.unpauseDateTime}
            />
            <Textarea
              name="pauseDescription"
              label={this.props.t('taskManagement.pauseTimerDescriptionLabel')}
              value={currentPause.pauseDescription}
              placeholder={this.props.t('forms.placeholders.enterTextHere')}
              onChange={this.onChangePauseDescription}
              maxLength={IRREGULARITIES_NOTES_MAX_LENGTH}
              error={errors.pauseDescription}
            />
          </section>
        </ModalInfo>
      </div>
    );
  }
}

AddPause.propTypes = {
  t: PropTypes.func.isRequired,
};

AddPause.defaultProps = {};

export default withTranslation()(AddPause);
